import type { OrchestratorSchemas, TrayLoad } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { createQuery } from '../query.ts'

type ControlView = OrchestratorSchemas['schemas']['ModuleControlView']
type LoadIdentity = Pick<TrayLoad, 'module_id' | 'batch_id' | 'load_id'>

/** 확약 창의 부가 자료 — 신고 칸 기본값과 그 적재가 선 모듈의 종류(둘 다 서버 응답에서만) */
export interface LoadConfirmDefault {
  /** 장부 건의 기대 챔버 수(`tray_confirm.load_confirm.entries[].expected_chambers`). 없으면 `null` */
  expectedChambers: number | null
  /**
   * 그 모듈의 종류(`module.module_type`). 🔴 신고 칸(`loaded_chambers`)은 **원심 C 트레이 반입에만** 있다 —
   * 계약 `loaded_chambers` 「원심 C 트레이만 · 빈 A·L 은 싣지 않는다」. `expected_chambers` 는 배치 값이라
   * 같은 배치의 염색기 · 어셈블러 건에도 실려 오므로 종류는 이 값으로 가른다(eric 2026-10-29).
   */
  moduleType: string | null
}

/**
 * 확약 창을 열 때 읽는 부가 자료. GET /modules/{id}/control (operator · admin · viewer).
 * TrayLoad에는 기대 수 · 모듈 종류가 없다. 서버가 배치 문서에서 낸 장부 건의 값과 그 응답의 모듈 스냅숏만 사용한다.
 * 근거: views/module_control.py _open_ledger · dispatch/targets.py expected_chambers_of · `ModuleControlView.module`.
 * 요청 실패는 호출부에서 처리하며 확약 창 자체를 막지 않는다. 폴링 없음.
 */
export const useLoadConfirmDefault = createQuery<LoadIdentity, LoadConfirmDefault>({
  key: ({ module_id, batch_id, load_id }) => [
    ...keys.modules.detail(module_id),
    'load-confirm-default',
    batch_id,
    load_id,
  ],
  fetch: async ({ module_id, batch_id, load_id }) => {
    const view = await orchestrator.request<ControlView>({
      method: 'GET',
      path: `/modules/${encodeURIComponent(module_id)}/control`,
      retryOn401: true,
    })
    const entry = view.tray_confirm.load_confirm?.entries.find(
      (entry) => entry.id === load_id && entry.batch_id === batch_id,
    )
    const expected = entry?.expected_chambers
    return {
      expectedChambers:
        expected != null && Number.isSafeInteger(expected) && expected >= 0 ? expected : null,
      // ⚠ 부분 응답(시험 목)에서 `module` 이 없을 수 있다 — 모르면 `null`(칸을 세우지 않는다)
      moduleType: view.module?.module_type ?? null,
    }
  },
})
